import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { isCommitmentMove, type DeskMove } from '@/lib/deskNextMove';
import { recordClickBeforeNavigate } from '@/lib/nextMoveEvents';

/** The single reader of Your Next Move™ (get_desk_next_move). Ribbon and Your Desk share this cache. */
export function useNextMove(enabled = true) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const key = ['desk-next-move', user?.id];
  const q = useQuery({
    queryKey: key,
    enabled: !!user && enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_desk_next_move' as never);
      if (error) throw error;
      return (data as unknown as DeskMove | null) ?? null;
    },
  });
  const dismiss = useMutation({
    mutationFn: async ({ ruleKey, surface, memoryId }: { ruleKey: string; surface: string; memoryId?: string }) => {
      // A personal commitment closes only that promise for its owner — never a company-wide snooze.
      if (isCommitmentMove(ruleKey)) {
        if (!memoryId) return;
        const { error } = await supabase.rpc('complete_desk_commitment' as never, { p_memory_id: memoryId } as never);
        if (error) throw error;
        return;
      }
      const { error } = await supabase.rpc('dismiss_desk_next_move' as never, { p_rule_key: ruleKey, p_surface: surface } as never);
      if (error) throw error;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['desk-next-move', user?.id] }),
  });
  /** "Not interested" — this person only. Server pauses it (admin-set days), and after enough repeats mutes it for good. */
  const mute = useMutation({
    mutationFn: async ({ ruleKey }: { ruleKey: string }) => {
      const { data, error } = await supabase.rpc('mute_desk_next_move' as never, { p_rule_key: ruleKey } as never);
      if (error) throw error;
      return data as unknown as 'paused' | 'muted';
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['desk-next-move', user?.id] });
      qc.invalidateQueries({ queryKey: ['desk-next-move-deck'] });
    },
  });
  /** Resolves once the click is stored (or after a short bound), so callers navigate afterwards. */
  const recordClick = (ruleKey: string, surface: string) => recordClickBeforeNavigate(ruleKey, surface);
  return { move: q.data ?? null, isLoading: q.isLoading, dismiss, mute, recordClick };
}
